import { useMemo } from "react";
import styled from "@emotion/styled";

import Tooltip from "components/ui/tooltip/Tooltip";

export type UserInitialsType = "default" | "small" | "extra-small";

export type UserInitialsColour =
  | "pink"
  | "turquoise"
  | "yellow"
  | "blue"
  | "gold"
  | "dark-blue"
  | "green"
  | "orange"
  | "light-green"
  | "red"
  | "grey";

type Props = {
  firstName?: string;
  lastName?: string;
  showTooltip?: boolean;
  className?: string;
  type?: UserInitialsType;
  color?: UserInitialsColour;
};

export const colours: UserInitialsColour[] = [
  "pink",
  "turquoise",
  "yellow",
  "blue",
  "gold",
  "dark-blue",
  "green",
  "orange",
  "light-green",
  "red",
];

function getUserColour(firstName?: string, lastName?: string) {
  const first = firstName?.charCodeAt(0) ?? 0;
  const second = lastName?.charCodeAt(0) ?? 0;
  const userNumber = first + second;
  const colourIndex = userNumber % colours.length;
  return colours[colourIndex];
}

// Color map matching the original SCSS color values
const colorMap: Record<UserInitialsColour, string> = {
  turquoise: "#29D4B1",
  yellow: "#EEBA2A",
  blue: "#29B5D4",
  "dark-blue": "#298CD4",
  green: "#32BE59",
  gold: "#CD911F",
  orange: "#D46729",
  "light-green": "#29D48C",
  pink: "#D42967",
  red: "#D42929",
  grey: "#F7F7F8", // sysBackgroundAlternative
};

const UserInitialsContainer = styled.div<{
  color: UserInitialsColour;
}>(({ theme, color }) => ({
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  backgroundColor: colorMap[color],

  "& span": {
    color: color === "grey" ? theme.colors.sysTextDefault : "#FFFFFF",
    fontWeight: color === "grey" ? 500 : "normal",
    lineHeight: 1,
  },

  "&[data-type='default']": {
    height: 32,
    width: 32,
    fontSize: 14,
  },

  "&[data-type='small']": {
    height: 24,
    width: 24,
    fontSize: 12,
  },

  "&[data-type='extra-small']": {
    height: 20,
    width: 20,
    fontSize: 10,

    "& span": {
      transform: "translateY(0.5px)",
    },
  },
}));

function UserInitials({
  className,
  showTooltip = true,
  firstName,
  lastName,
  color,
  type = "default",
}: Props): JSX.Element {
  const initials = (firstName?.charAt(0) || "") + (lastName?.charAt(0) || "");
  const userColour = useMemo(
    () => color || getUserColour(firstName, lastName),
    [color, firstName, lastName],
  );

  const container = (
    <UserInitialsContainer color={userColour} data-type={type} className={className}>
      <span>{initials.toUpperCase()}</span>
    </UserInitialsContainer>
  );

  if (!showTooltip) {
    return container;
  }

  return <Tooltip content={`${firstName} ${lastName}`}>{container}</Tooltip>;
}

export default UserInitials;
